import { COMPLIANCE_TEMPLATES } from './templates';
import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { EditorModule } from '@tinymce/tinymce-angular';

import { ButtonModule } from '@lockton-apps/shared/ui/lui';

import { DocumentBuilderPageDirective } from '../../../document-builder/document-builder-page.base.directive';
import { TINYMCE_EDITOR_OPTIONS } from '../../../providers';
import { DOCUMENT_TEMPLATES } from '../../templates';
import { Partners } from '@lockton-apps/shared/interfaces';

@Component({
    selector: 'la-pnc-risk-review-front',
    template: `
    <form [formGroup]="formGroup">
      <div class="zone-dark doc-page doc-page-full cover">
        <div class="cover-image">
          <img src="/assets/images/cover-photo2.jpg" alt="" />
        </div>
        <main>
          <div class="cover__panel">
            <div class="cover-title">Risk Review</div>
            <div class="cover-subtitle">{{ clientName }}</div>
            <div class="cover__subtitle">
              <editor
                [apiKey]="editorOptions.apiKey"
                [inline]="true"
                [plugins]="editorOptions.plugins"
                [init]="editorOptions.options"
                [toolbar]="editorOptions.toolbar"
                formControlName="subtitle"
              ></editor>
            </div>
          </div>
        </main>
        <aside>
          <img
            src="/assets/ui/lockton_combined.svg"
            alt="demo document logo with triangle shapes"
            width="160"
            height="106"
          />
        </aside>
      </div>

      <div class="doc-page info">
        <editor
          [apiKey]="editorOptions.apiKey"
          [inline]="true"
          [plugins]="editorOptions.plugins"
          [init]="editorOptions.options"
          [toolbar]="editorOptions.toolbar"
          formControlName="disclosure"
        ></editor>
      </div>
    </form>
  `,
    imports: [
        CommonModule,
        EditorModule,
        ReactiveFormsModule,
        MatIconModule,
        ButtonModule,
        MatDividerModule,
    ],
    styles: [
        `
      .cover {
        position: relative;
        background: linear-gradient(
          90deg,
          rgba(0, 161, 230, 1) 0%,
          rgba(0, 161, 230, 1) 34%,
          rgba(0, 0, 0, 1) 34%,
          rgba(0, 0, 0, 1) 100%
        );
        display: grid;
        grid-template-columns: 1fr 4.2cm;
        grid-template-rows: 1fr;
        gap: 0.5cm;
        padding: 2cm;
        z-index: 0;
      }

      .cover-image {
        position: absolute;
        top: 0;
        left: 34%;
        right: 0;
        bottom: 0;
        background-size: cover;
        z-index: -1;

        img {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          object-fit: cover;
        }
      }

      .cover__subtitle {
        display: inline-block;
        padding: 0.2cm 0.4cm;
        background-color: #000;
        color: #fff;
        margin-top: 1.5cm;
        min-width: 5cm;
      }

      .cover__panel {
        background-color: #fff;
        color: #000;
        padding: 1cm;
      }

      .cover aside {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
      }

      .info {
        page-break-before: always;
      }

      @media screen {
        .doc-page {
          margin-top: 1cm;
          width: 21cm;
        }

        .info {
          min-height: 29.7cm;
          padding: 2cm;
          background-color: #fff;
          width: 21cm;
        }
      }
    `,
    ]
})
export class PncRiskReviewFrontComponent extends DocumentBuilderPageDirective {
  editorOptions = inject(TINYMCE_EDITOR_OPTIONS);

  override pageId = DOCUMENT_TEMPLATES[Partners.PNC]['pnc-rr'].front?.id || '';

  @Input() clientName: string = '';
  @Input() get editorValues() {
    return this._editorValues;
  }
  set editorValues(values: Record<string, string>) {
    this._editorValues = { ...values };
    this.formGroup.patchValue(values);
  }
  private _editorValues: Record<string, string> = {};

  get summary() {
    return this.formGroup.get('summary') as unknown as FormArray<FormGroup>;
  }

  constructor() {
    super();
    this.addControls({
      subtitle: new FormControl(''),
      disclosure: new FormControl(''),
    });
  }
}
